import { useState } from "react";
import { Loader2, Send } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { formatApiError, sendEnquiry } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

const inputCls =
  "mt-1.5 w-full rounded-md border border-input bg-white/60 px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary transition";

const initial = {
  company: "",
  gstin: "",
  name: "",
  email: "",
  phone: "",
  city: "",
  product: "",
  quantity: "",
  delivery_date: "",
  message: "",
};

export const BulkQuoteDialog = ({ open, onOpenChange }) => {
  const [form, setForm] = useState(initial);
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await sendEnquiry({
        name: form.name,
        email: form.email,
        phone: form.phone,
        subject: "Bulk / Trade enquiry",
        message: form.message || `Bulk quote request from ${form.company}`,
        company: form.company,
        gstin: form.gstin || null,
        product: form.product,
        quantity: form.quantity ? Number(form.quantity) : null,
        delivery_date: form.delivery_date || null,
      });
      toast.success("Quote request sent — our team will call you within 1 business day");
      setForm(initial);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="bulk-quote-dialog" className="max-w-2xl bg-background max-h-[90vh] overflow-y-auto">
        <DialogTitle className="font-serif text-2xl md:text-3xl font-semibold tracking-tight">Request a bulk quote</DialogTitle>
        <p className="text-sm text-muted-foreground mt-1">
          Manufacturer-direct pricing for schools, offices, retailers and corporates. We respond within one business day.
        </p>
        <form onSubmit={submit} className="mt-6 grid sm:grid-cols-2 gap-4">
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-[0.1em] text-muted-foreground">Company / School name</span>
            <input data-testid="bq-company" required value={form.company} onChange={set("company")} placeholder="Sunrise Public School" className={inputCls} />
          </label>
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-[0.1em] text-muted-foreground">GSTIN (optional)</span>
            <input data-testid="bq-gstin" value={form.gstin} onChange={set("gstin")} placeholder="27ABCDE1234F1Z5" className={inputCls} />
          </label>
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-[0.1em] text-muted-foreground">Contact person</span>
            <input data-testid="bq-name" required value={form.name} onChange={set("name")} placeholder="Your name" className={inputCls} />
          </label>
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-[0.1em] text-muted-foreground">Phone / WhatsApp</span>
            <input data-testid="bq-phone" required type="tel" pattern="[0-9+ -]{10,15}" value={form.phone} onChange={set("phone")} placeholder="+91 98765 43210" className={inputCls} />
          </label>
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-[0.1em] text-muted-foreground">Email</span>
            <input data-testid="bq-email" required type="email" value={form.email} onChange={set("email")} placeholder="you@company.com" className={inputCls} />
          </label>
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-[0.1em] text-muted-foreground">City</span>
            <input data-testid="bq-city" required value={form.city} onChange={set("city")} placeholder="Pune" className={inputCls} />
          </label>
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-[0.1em] text-muted-foreground">Products needed</span>
            <input data-testid="bq-product" required value={form.product} onChange={set("product")} placeholder="e.g. A5 notebooks, box files" className={inputCls} />
          </label>
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-[0.1em] text-muted-foreground">Approx. quantity (pcs)</span>
            <input data-testid="bq-quantity" required type="number" min="1" value={form.quantity} onChange={set("quantity")} placeholder="500" className={inputCls} />
          </label>
          <label className="block sm:col-span-2">
            <span className="text-xs font-bold uppercase tracking-[0.1em] text-muted-foreground">Required by (optional)</span>
            <input data-testid="bq-date" type="date" value={form.delivery_date} onChange={set("delivery_date")} className={inputCls} />
          </label>
          <label className="block sm:col-span-2">
            <span className="text-xs font-bold uppercase tracking-[0.1em] text-muted-foreground">Message / customisation (optional)</span>
            <textarea data-testid="bq-message" rows={3} value={form.message} onChange={set("message")} placeholder="Custom covers, ruling, delivery notes…" className={`${inputCls} resize-none`} />
          </label>
          <button
            data-testid="bq-submit"
            type="submit"
            disabled={sending}
            className="sm:col-span-2 rounded-full bg-primary text-primary-foreground py-3.5 text-sm font-bold flex items-center justify-center gap-2 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg disabled:opacity-60"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {sending ? "Sending…" : "Request a quote"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
